'use client';

import React, {
  ReactNode,
  useEffect,
  useState,
  useRef,
  useImperativeHandle,
  forwardRef,
  ForwardRefExoticComponent,
  RefAttributes,
  useCallback,
} from 'react';
import { animated, useSpring } from '@react-spring/web';
import { useTabsDrag, DragTabProvider } from '../hooks/useTabsDrag';
import { useLocale } from 'use-intl';
import { ChevronRightSIcon } from 'tdesign-icons-react';
import { useMedia } from '@ssv-website/hooks';

export const TabPane = ({
  tabKey,
  title,
  children,
  className = '',
}: {
  tabKey: string;
  title:
    | React.ReactNode
    | (({ isActive }: { isActive: boolean }) => React.ReactNode);
  children: React.ReactNode;
  className?: string;
}) => {
  const tabPaneRef = useRef<HTMLDivElement>(null);
  const [indicatorLeft, setIndicatorLeft] = useState(0);

  // 监听滚动事件，实时更新 scrollLeft
  useEffect(() => {
    const handleScroll = () => {
      if (tabPaneRef.current) {
        updateIndicatorLeft();
      }
    };

    const updateIndicatorLeft = () => {
      const getIndicatorLeft = () => {
        if (!tabPaneRef.current) return 0;
        const { scrollWidth, clientWidth, scrollLeft } = tabPaneRef.current;
        const scrollableWidth = scrollWidth - clientWidth; // 可滚动的总宽度（内容宽度 - 容器可见宽度）
        if (scrollableWidth === 0) return -1; // 无滚动时，不展示
        const ratio = scrollLeft / scrollableWidth; // 滚动比例（0~1）
        const parentWidth = 24; // 白色条父容器的宽度
        const indicatorWidth = 8; // 白色条自身宽度
        const maxLeft = parentWidth - indicatorWidth; // 白色条可移动的最大范围
        return maxLeft * ratio; // 最终 left 偏移量
      };
      const left = getIndicatorLeft();
      setIndicatorLeft(left);
    };

    updateIndicatorLeft();

    if (tabPaneRef.current) {
      tabPaneRef.current.addEventListener('scroll', handleScroll);
      window.addEventListener('resize', handleScroll);
    }

    return () => {
      if (tabPaneRef.current) {
        tabPaneRef.current.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', handleScroll);
      }
    };
  }, []);

  return (
    <>
      <div
        className={`${
          indicatorLeft !== -1 ? 'mobile:overflow-x-auto scrollbar-hidden' : ''
        } ${className}`}
        ref={tabPaneRef}
      >
        {children}
      </div>

      {indicatorLeft !== -1 && (
        <div className="relative bg-[#121C5933] h-[2px] w-[24px] mt-[24px] mx-auto hidden mobile:block">
          <div
            className="absolute left-0 top-0 bg-[#2D2D2E] h-full w-[8px]"
            style={{
              left: `${indicatorLeft}px`,
            }}
          ></div>
        </div>
      )}
    </>
  );
};

const TabPaneHeader = ({
  data,
  activeKey,
  onChange,
  className = '',
  titleScrollClassName = '',
  titleContentClassName = '',
}: {
  data: any[];
  activeKey: string;
  onChange?: (key: string) => void;
  className?: string;
  titleScrollClassName?: string;
  titleContentClassName?: string;
}) => {
  const lineRef = useRef<HTMLDivElement>(null);
  const [tabWidth, setTabWidth] = useState(0);
  const [headerWidth, setHeaderWidth] = useState(0);
  const isMobile = useMedia('mobile');
  const tabRef = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const headerRef = useRef<HTMLDivElement>(null);
  const headerContentRef = useRef<HTMLDivElement>(null);
  const [tabPositions, setTabPositions] = useState<{ [key: string]: number }>(
    {}
  );
  const [isScrolledToEnd, setIsScrolledToEnd] = useState(false);
  const [isScroll, setIsScroll] = useState(false);
  // 计算黑色背景块的位置
  const activeIndex = data.findIndex(
    (item: React.ReactElement) => item.props.tabKey === activeKey
  );
  const scrollToActiveTab = () => {
    const headerEl = headerRef.current;
    if (!headerEl) return;

    // 获取关键参数（避免空值报错）
    const activeTabLeft = tabPositions[activeKey] || 0; // 选中标签相对父容器的left
    const activeTabW = tabWidth || 0; // 选中标签宽度
    const headerVisibleW = headerEl.clientWidth; // 父容器可视宽度（核心！区别于scrollWidth）
    const headerScrollW = headerEl.scrollWidth; // 父容器滚动总宽度
    // 计算目标scrollLeft（居中公式）
    const targetScrollLeft =
      activeTabLeft + activeTabW / 2 - headerVisibleW / 2;
    // 边界处理：避免scrollLeft为负或超出最大滚动范围
    const maxScrollLeft = headerScrollW - headerVisibleW;
    const safeScrollLeft = Math.max(
      0,
      Math.min(targetScrollLeft, maxScrollLeft)
    );

    // 平滑滚动到目标位置
    headerEl.scrollTo({
      left: safeScrollLeft,
      behavior: 'smooth', // 平滑滚动（可选，去掉则为瞬间跳转）
    });
  };
  const handleMoreBtn = () => {
    const headerEl = headerRef.current;
    if (!headerEl) return;
    const { clientWidth, scrollLeft } = headerEl;
    const moveLeft = scrollLeft + clientWidth / 2;

    headerEl.scrollTo({
      left: moveLeft,
      behavior: 'smooth', // 平滑滚动（可选，去掉则为瞬间跳转）
    });
  };
  // 计算每个标签的 left 位置（相对于父容器）
  const updateTabPositions = () => {
    if (!headerContentRef.current) return;
    // 获取父容器的位置信息（相对于视口）
    const headerRect = headerContentRef.current.getBoundingClientRect();
    const positions: { [key: string]: number } = {};
    const scrollLeft = headerContentRef.current.scrollLeft || 0;

    // 遍历所有标签，计算相对父容器的 left 偏移
    data.forEach((item: React.ReactElement) => {
      const tabKey = item.props.tabKey;
      const tabEl = tabRef.current[tabKey];
      if (tabEl) {
        const tabRect = tabEl.getBoundingClientRect();
        // 标签 left（视口） - 父容器 left（视口） = 相对父容器的 left 偏移
        positions[tabKey] = tabRect.left + scrollLeft - headerRect.left;
      }
    });
    setHeaderWidth(headerContentRef.current.offsetWidth);
    setTabPositions(positions);
    scrollToActiveTab();
  };
  useEffect(() => {
    const headerEl = headerRef.current;
    if (!headerEl) return;
    const { clientWidth, scrollWidth } = headerEl;
    setIsScroll(scrollWidth - clientWidth !== 0);
  }, []);
  useEffect(() => {
    adjustLineWidth();
    updateTabPositions();
  }, [activeKey, data]);

  useEffect(() => {
    const handleResize = () => {
      adjustLineWidth();
      updateTabPositions();
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (headerRef.current) {
        const { scrollWidth, clientWidth, scrollLeft } = headerRef.current;
        // 是否滚动到底
        const isAtEnd = scrollLeft + clientWidth >= scrollWidth - 5;
        setIsScrolledToEnd(isAtEnd); // 更新状态
      }
    };

    if (headerRef.current) {
      headerRef.current.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (headerRef.current) {
        headerRef.current.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  // 为黑色背景块创建动画
  const backgroundSpring = useSpring({
    transform: `translateX(${tabPositions[activeKey] || 0}px)`,
    config: { tension: 1000, friction: 100, clamp: true },
  });

  const adjustLineWidth = () => {
    if (tabRef.current[activeKey]) {
      const rect = tabRef.current[activeKey]?.getBoundingClientRect();
      setTabWidth(rect.width);
    }
  };

  return (
    <div className={`relative ${className}`}>
      <div
        className={`relative overflow-x-auto overflow-y-hidden scrollbar-hidden ${titleScrollClassName}`}
        ref={headerRef}
      >
        <div
          ref={headerContentRef}
          className={`relative w-fit flex flex-row ${titleContentClassName}`}
        >
          {/* line */}
          <div
            className="absolute left-0 bottom-0 h-px w-full bg-[#BECBE1]"
            ref={lineRef}
            style={{
              width: headerWidth,
            }}
          >
            <animated.div
              className="absolute left-0 bottom-0 h-1 rounded-full bg-[#121C59] mobile:h-[2px]"
              style={{
                width: tabWidth,
                transform: backgroundSpring.transform,
              }}
            />
          </div>
          {/* tab */}
          {data.map((item: React.ReactElement, index: number) => {
            const itemKey = item.props.tabKey;
            const isActive = index === activeIndex;
            return (
              <div
                key={itemKey}
                onClick={() => {
                  onChange?.(itemKey);
                }}
                ref={(el) => {
                  tabRef.current[itemKey] = el;
                }}
                className={`flex-shrink-0 flex items-center mr-16 last:mr-0 mobile:mr-5`}
              >
                <div className={`text-[32px] text-black text-nowrap`}>
                  {typeof item.props.title === 'function'
                    ? item.props.title({ isActive })
                    : item.props.title}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {!isMobile && isScroll && !isScrolledToEnd && (
        <div className="absolute right-0 top-0 bottom-0 w-[160px] mobile:w-[40px] bg-[linear-gradient(270deg,#EDF3FF_10%,#EDF3FF00_100%)] flex justify-end items-center">
          <div
            className={`size-7 text-[#121C59] flex items-center justify-center cursor-pointer mr-2`}
            onClick={() => {
              handleMoreBtn();
            }}
          >
            <ChevronRightSIcon size={28}></ChevronRightSIcon>
          </div>
        </div>
      )}
    </div>
  );
};

export interface TabsRef {
  adjustTabHeight: () => void;
  onSwitchTab: (key: string) => void;
}
interface TabsProps {
  children: React.ReactNode;
  activeKey: string;
  onChange?: (key: string) => void;
  tabHeader?: ({ data, activeKey, onChange }: any) => ReactNode;
  titleStyle?: React.CSSProperties;
  defaultHeight?: number;
  disableDrag?: boolean;
  className?: string;
  openSticky?: boolean;
  stickyOffset?: number;
  titleClassName?: string;
  titleScrollClassName?: string;
  titleContentClassName?: string;
  paneScrollClassName?: string;
}

const ForwardRefTabs = forwardRef<TabsRef, TabsProps>(
  (
    {
      children,
      activeKey,
      onChange,
      tabHeader,
      defaultHeight = 336,
      disableDrag = false,
      className = '',
      paneScrollClassName = '',
      titleClassName = '',
      titleScrollClassName = '',
      titleContentClassName = '',
      openSticky = false,
      stickyOffset = 0,
    },
    ref
  ) => {
    const panes = React.Children.toArray(children);
    const [currentKey, setCurrentKey] = useState(activeKey);
    const tabRef = useRef<{ [key: string | number]: HTMLDivElement | null }>(
      {}
    );
    const tabsContainerRef = useRef<HTMLDivElement>(null);
    const [tabHeight, setTabHeight] = useState(defaultHeight);
    const locale = useLocale();
    // 获取当前标签页索引
    const currentIndex =
      panes.findIndex(
        (pane) => (pane as React.ReactElement).props.tabKey === currentKey
      ) || 0;
    const currRef = useRef(currentIndex);
    currRef.current = currentIndex;

    const scrollToElementWithOffset = (offset = 0) => {
      if (!tabsContainerRef.current) return;
      const rect = tabsContainerRef.current.getBoundingClientRect();
      const targetScrollTop = window.scrollY + rect.top - offset;
      window.scrollTo({
        top: targetScrollTop,
        behavior: 'auto',
      });
    };

    const handleChange = useCallback(
      (key: string) => {
        if (tabsDrag.isDragging) return; // 拖拽时不响应点击
        // 如果开启了吸顶，则滚动到对应位置
        if (openSticky) {
          scrollToElementWithOffset(stickyOffset);
        }
        setCurrentKey(key);
        onChange?.(key);
      },
      [onChange, openSticky]
    );

    // 使用抽象的拖拽钩子，包含动画逻辑
    const tabsDrag = useTabsDrag({
      disableDrag,
      currentIndex,
      totalTabs: panes.length,
      onTabChange: (newIndex) => {
        // 更新状态
        if (newIndex !== currentIndex) {
          const newKey = (panes[newIndex] as React.ReactElement).props.tabKey;
          setCurrentKey(newKey);
          onChange?.(newKey);
        }
      },
    });

    const adjustTabHeight = useCallback(() => {
      if (tabRef.current[currRef.current]) {
        const rect = tabRef.current[currRef.current]?.getBoundingClientRect();
        if (rect && rect.width > 0) {
          setTabHeight(rect.height);
        }
      }
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        adjustTabHeight: adjustTabHeight, // 挂载要暴露的方法
        onSwitchTab: handleChange,
      }),
      [adjustTabHeight, handleChange]
    ); // 依赖项：adjustTabHeight 若依赖其他变量需添加

    useEffect(() => {
      if (tabRef.current[currentIndex]) {
        adjustTabHeight();
      }
    }, [adjustTabHeight, currentIndex, locale]);

    useEffect(() => {
      window.addEventListener('resize', adjustTabHeight);
      return () => window.removeEventListener('resize', adjustTabHeight);
    }, []);

    return (
      <DragTabProvider
        currentIndex={currentIndex}
        totalTabs={panes.length}
        dragProgress={tabsDrag.dragProgress}
      >
        <div className={`${className}`} ref={tabsContainerRef}>
          {tabHeader ? (
            tabHeader({
              data: panes,
              activeKey: currentKey,
              onChange: handleChange,
            })
          ) : (
            <TabPaneHeader
              data={panes}
              activeKey={currentKey}
              onChange={handleChange}
              className={
                titleClassName + `${openSticky ? ' sticky top-0 z-10' : ''}`
              }
              titleScrollClassName={titleScrollClassName}
              titleContentClassName={titleContentClassName}
            />
          )}
          <div
            ref={tabsDrag.containerRef}
            className={`relative overflow-hidden w-full ${
              disableDrag
                ? 'touch-auto'
                : tabsDrag.isDragging
                ? 'touch-none'
                : 'touch-pan-y'
            }`}
            style={{
              height: tabHeight,
            }}
            {...tabsDrag.bind()}
          >
            {panes.map((pane, index) => {
              if (React.isValidElement(pane)) {
                const { tabKey, title, children } = (pane as React.ReactElement)
                  .props;
                const spring = tabsDrag.springs[index];

                return (
                  <animated.div
                    key={tabKey}
                    className="absolute top-0 left-0 w-full"
                    ref={(el) => {
                      tabRef.current[index] = el!;
                    }}
                    style={{
                      transform: spring.x.to((x) => `translateX(${x}%)`),
                      opacity: spring.opacity,
                    }}
                  >
                    <TabPane
                      className={paneScrollClassName}
                      tabKey={tabKey}
                      title={title}
                    >
                      {children}
                    </TabPane>
                  </animated.div>
                );
              }
              return null;
            })}
          </div>
        </div>
      </DragTabProvider>
    );
  }
);
type TabsComponent = ForwardRefExoticComponent<
  TabsProps & RefAttributes<TabsRef>
> & {
  Pane: typeof TabPane; // 明确 Pane 是 TabPane 组件的类型
};
const Tabs = ForwardRefTabs as unknown as TabsComponent;
Tabs.Pane = TabPane;
export default Tabs;
